import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Badge } from "./ui/badge";
import {
  Users, Activity, ChevronDown, ChevronUp, RefreshCw, Circle, Building2,
  FileSearch, AlertTriangle, FileText, Shield, GraduationCap, Zap, Clock,
} from "lucide-react";

const POLL_INTERVAL = 30000; // 30 seconds

const MODULE_ICONS = {
  bia: FileSearch,
  risk: AlertTriangle,
  plan: FileText,
  policy: Shield,
  training: GraduationCap,
  exercise: Zap,
};

const ROLE_COLORS = {
  bcm_manager: "bg-sky-700",
  process_manager: "bg-emerald-500",
  app_manager: "bg-blue-500",
  it_function_manager: "bg-teal-500",
  it_drp_manager: "bg-cyan-500",
  process_contributor: "bg-slate-400",
  app_contributor: "bg-slate-400",
  it_function_contributor: "bg-slate-400",
  admin: "bg-amber-500",
};

function timeAgo(ts) {
  if (!ts) return "";
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function initials(name) {
  return (name || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export default function TeamActivityPanel() {
  const { api } = useAuth();
  const [expanded, setExpanded] = useState(true);
  const [activeUsers, setActiveUsers] = useState([]);
  const [feed, setFeed] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [collaborations, setCollaborations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const intervalRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const [usersRes, feedRes, deptRes, collabRes] = await Promise.all([
        api.get("/team-activity/active-users"),
        api.get("/team-activity/feed?limit=12"),
        api.get("/team-activity/department-workload?days=7"),
        api.get("/team-activity/collaboration"),
      ]);
      setActiveUsers(usersRes.data.users || []);
      setFeed(feedRes.data.activities || []);
      setDepartments(deptRes.data.departments || []);
      setCollaborations(collabRes.data.collaborations || []);
      setLastRefresh(new Date());

      // Send heartbeat
      api.post("/team-activity/heartbeat").catch(() => {});
    } catch (e) {
      console.error("Team activity fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchAll();
    intervalRef.current = setInterval(fetchAll, POLL_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [fetchAll]);

  const maxActivity = Math.max(1, ...departments.map((d) => d.activity_count));

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="team-activity-panel">
      {/* Header — always visible */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 transition-colors"
        data-testid="team-activity-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-700 to-teal-600 flex items-center justify-center shadow-sm">
            <Activity className="w-4 h-4 text-white" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-bold text-slate-800" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Team Activity
            </h3>
            <p className="text-[10px] text-slate-400">
              {activeUsers.length} active &bull; Refreshes every 30s
              {lastRefresh && <> &bull; {timeAgo(lastRefresh.toISOString())}</>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Active user dots preview */}
          <div className="flex -space-x-1.5">
            {activeUsers.slice(0, 4).map((u, i) => (
              <div
                key={u.id}
                className={`w-6 h-6 rounded-full ${ROLE_COLORS[u.role] || "bg-slate-400"} border-2 border-white flex items-center justify-center text-[8px] font-bold text-white`}
                title={u.name}
              >
                {initials(u.name)}
              </div>
            ))}
            {activeUsers.length > 4 && (
              <div className="w-6 h-6 rounded-full bg-slate-300 border-2 border-white flex items-center justify-center text-[8px] font-bold text-white">
                +{activeUsers.length - 4}
              </div>
            )}
          </div>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </div>
      </button>

      {/* Expandable body */}
      {expanded && (
        <div className="border-t border-slate-200 px-5 py-4 space-y-5 animate-fade-in">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-5 h-5 animate-spin text-sky-700" />
            </div>
          ) : (
            <>
              {/* ── Active Users ── */}
              <div data-testid="active-users-section">
                <h4 className="text-[0.6875rem] font-bold text-slate-500 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                  <span className="w-[7px] h-[7px] rounded-full bg-emerald-500 animate-pulse" />
                  Active Now ({activeUsers.length})
                </h4>
                {activeUsers.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {activeUsers.map((u) => (
                      <div
                        key={u.id}
                        className="flex items-center gap-2 px-3 py-1.5 border border-slate-200 rounded-lg bg-slate-50 hover:bg-white transition-colors"
                        data-testid={`active-user-${u.id}`}
                      >
                        <div className={`w-7 h-7 rounded-full ${ROLE_COLORS[u.role] || "bg-slate-400"} flex items-center justify-center text-[9px] font-bold text-white relative`}>
                          {initials(u.name)}
                          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-white" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-slate-800 leading-tight">{u.name}</p>
                          <p className="text-[10px] text-slate-400">
                            {u.current_action || u.role?.replace(/_/g, " ")}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">No users active in the last 30 minutes</p>
                )}
              </div>

              {/* ── Collaboration Indicators ── */}
              {collaborations.length > 0 && (
                <div data-testid="collaboration-section">
                  <h4 className="text-[0.6875rem] font-bold text-slate-500 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                    <span className="w-[7px] h-[7px] rounded-full bg-purple-500" />
                    Active Collaborations
                  </h4>
                  <div className="space-y-1.5">
                    {collaborations.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg text-xs">
                        <Users className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
                        <span className="text-purple-700 font-medium">
                          {c.user_count} users on{" "}
                          <span className="font-bold">{c.resource_type}</span>
                        </span>
                        <span className="text-purple-400">
                          ({c.user_names.join(", ")})
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Department Workload ── */}
              {departments.length > 0 && (
                <div data-testid="department-workload-section">
                  <h4 className="text-[0.6875rem] font-bold text-slate-500 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                    <span className="w-[7px] h-[7px] rounded-full bg-blue-500" />
                    Department Activity (7 days)
                  </h4>
                  <div className="space-y-1.5">
                    {departments.slice(0, 6).map((d) => (
                      <div key={d.department} className="flex items-center gap-3" data-testid={`dept-${d.department}`}>
                        <div className="w-24 text-[11px] text-slate-600 font-medium truncate" title={d.department}>
                          {d.department}
                        </div>
                        <div className="flex-1 h-[6px] bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all bg-gradient-to-r from-blue-400 to-teal-500"
                            style={{ width: `${Math.max(3, (d.activity_count / maxActivity) * 100)}%` }}
                          />
                        </div>
                        <div className="text-[10px] text-slate-400 w-12 text-right font-mono">
                          {d.activity_count} <span className="text-slate-300">&bull;</span> {d.user_count}u
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Live Activity Feed ── */}
              <div data-testid="activity-feed-section">
                <h4 className="text-[0.6875rem] font-bold text-slate-500 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                  <span className="w-[7px] h-[7px] rounded-full bg-sky-700" />
                  Recent Activity
                </h4>
                {feed.length > 0 ? (
                  <div className="space-y-0 max-h-[280px] overflow-y-auto pr-1">
                    {feed.map((item) => {
                      const moduleKey = (item.resource_type || "").split("_")[0];
                      const Icon = MODULE_ICONS[moduleKey] || Activity;
                      return (
                        <div
                          key={item.id}
                          className="flex items-start gap-2.5 py-2 border-b border-slate-100 last:border-0"
                          data-testid={`feed-item-${item.id}`}
                        >
                          <div className={`w-6 h-6 rounded-full ${ROLE_COLORS[item.user_role] || "bg-slate-400"} flex items-center justify-center text-[8px] font-bold text-white flex-shrink-0 mt-0.5`}>
                            {initials(item.user_name)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-slate-700">
                              <span className="font-semibold">{item.user_name}</span>{" "}
                              <span className="text-slate-500">{item.action_description}</span>
                            </p>
                            {item.resource_type && (
                              <p className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5">
                                <Icon className="w-3 h-3" />
                                {item.resource_type.replace(/_/g, " ")}
                                {item.resource_id && <> &bull; {item.resource_id.slice(0, 8)}</>}
                              </p>
                            )}
                          </div>
                          <span className="text-[10px] text-slate-400 flex-shrink-0 mt-0.5">
                            {timeAgo(item.timestamp)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic py-4 text-center">
                    No recent activity in the last 48 hours
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
